import React, { useState } from 'react';
import ScrollArea from '../../components/common/scrollArea';
import { Divider } from '../../components/sidebar/components/Divider';
import slackIcon from '../../assets/slack-icon.png';
import { useSlackIntegration } from '../../hooks/useSlackIntegration';
import ChannelSelector from '../../components/common/channelSelector';
import Toast from '../../components/common/Toast';

const IntegrationsPage = () => {
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [showToast, setShowToast] = useState(false);
  
  const { 
    status, 
    channels,
    isLoading, 
    isLoadingChannels,
    disconnect, 
    initiateSlackConnection,
    setChannel 
  } = useSlackIntegration();

  const handleConnectSlack = () => {
    if (!status.connected) {
      initiateSlackConnection();
    }
  };

  const handleManageChannels = () => {
    setShowChannelSelector(true);
  };

  const handleChannelSelect = async (channelId: string) => {
    try {
      await setChannel(channelId, {
        onSuccess: () => {
          setShowChannelSelector(false);
          setShowToast(true);
        },
        onError: (error) => {
          console.error('Failed to set channel:', error);
        }
      });
    } catch (error) {
      console.error('Failed to set channel:', error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col w-[762px] text-[Nunito Sans]">
        {/* Header */}
        <div className="flex items-start mt-5 mb-2">
          <div className="text-start text-sm font-light text-[#75777C]">
            Workspace settings
          </div>
          <div className="text-start text-sm font-light text-[#222222] pl-2">
            / Integrations
          </div>
        </div>

        <ScrollArea maxHeight="calc(100vh - 80px)">
          <div className="flex flex-col">
            {/* Page Title */}
            <div className="flex flex-col mb-7">
              <div className="flex flex-col mt-5 text-left gap-[2px]">
                <div className="text-xl font-semibold text-[#222222]">Integrations</div>
                <div className="text-sm font-light text-[#75777C]">
                  Select and connect tools you want to integrate with your flow
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] mb-7" />

            {/* Integrations List */}
            <div className="flex flex-col space-y-4">
              {/* Slack Integration */}
              <div className="flex items-center justify-between p-4 border border-[#EEEFF1] rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <img src={slackIcon} alt="Slack" className="w-6 h-6" />
                  </div>
                  <div className='text-left'>
                    <h3 className="text-base font-semibold text-[#222222]">Slack</h3>
                    <p className="text-sm font-medium text-[#75777C]">
                      Messaging app for business that connects people to the information they need.
                    </p>
                    {status.connected && status.channel_id && (
                      <p className="text-xs text-[#29DC5B] mt-1">
                        Connected to #{status.channel_name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {status.connected ? (
                    <>
                      <button
                        onClick={() => disconnect()}
                        className="px-4 py-1 text-sm text-[#222222] bg-[#EEEFF1] rounded-lg hover:bg-[#E5E5E5] transition-colors"
                      >
                        Disconnect
                      </button>
                      <button
                        onClick={handleManageChannels}
                        className="px-4 py-1 text-sm text-[#222222] bg-[#EEEFF1] rounded-lg hover:bg-[#E5E5E5] transition-colors"
                      >
                        Manage
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleConnectSlack}
                      className="px-4 py-1 text-sm text-[#222222] bg-[#29DC5B] rounded-lg hover:bg-[#22c55e] transition-colors"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>

      <ChannelSelector
        isOpen={showChannelSelector}
        onClose={() => setShowChannelSelector(false)}
        onSelect={handleChannelSelect}
        channels={channels}
        isLoading={isLoadingChannels}
        selectedChannelId={status.channel_id}
      />

      <Toast 
        message="Slack channel updated successfully"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type="success"
      />
    </div>
  );
};

export default IntegrationsPage;
